import axios, { AxiosInstance, AxiosRequestConfig } from "axios"
import axiosRetry from "axios-retry"
import toast from "react-hot-toast"

export type NetworkRequestIntent = "active" | "background"

const OFFLINE_TOAST_ID = "network-loss-error"
const OFFLINE_TOAST_MESSAGE = "Network connection lost. Please check your internet and try again."
export const ACTIVE_REQUEST_METHODS = new Set(["post", "put", "patch", "delete"])
export const DEFAULT_BACKGROUND_RETRIES = 3
export const BASE_NETWORK_RETRY_DELAY_MS = 300

export const inferNetworkRequestIntentFromMethod = (method?: string): NetworkRequestIntent => {
	const normalizedMethod = method?.toLowerCase()
	return normalizedMethod && ACTIVE_REQUEST_METHODS.has(normalizedMethod)
		? "active"
		: "background"
}

export const getNetworkRequestIntent = (config?: AxiosRequestConfig): NetworkRequestIntent => {
	if (config?.networkIntent === "active" || config?.networkIntent === "background") {
		return config.networkIntent
	}

	return inferNetworkRequestIntentFromMethod(config?.method)
}

export const isBrowserOffline = (): boolean => {
	return typeof navigator !== "undefined" && navigator.onLine === false
}

export const isNetworkConnectivityError = (error: unknown): boolean => {
	if (
		typeof error === "object" &&
		error !== null &&
		"isNetworkError" in error &&
		error.isNetworkError === true
	) {
		return true
	}

	if (axios.isAxiosError(error)) {
		return axiosRetry.isNetworkError(error)
	}

	if (error instanceof TypeError) {
		return /failed to fetch|networkerror|load failed/i.test(error.message)
	}

	if (error instanceof Error) {
		return /network|offline|internet connection/i.test(error.message)
	}

	return false
}

export const showActiveNetworkErrorToast = (): void => {
	toast.error(OFFLINE_TOAST_MESSAGE, { id: OFFLINE_TOAST_ID, duration: 2500 })
}

export const attachAxiosNetworkBehavior = (
	client: AxiosInstance,
	options?: { backgroundRetries?: number },
): void => {
	const backgroundRetries = options?.backgroundRetries ?? DEFAULT_BACKGROUND_RETRIES

	// Background requests (sync/prefetch/query polling) retry silently on network loss.
	axiosRetry(client, {
		retries: backgroundRetries,
		retryDelay: (retryCount, error) =>
			axiosRetry.exponentialDelay(retryCount, error, BASE_NETWORK_RETRY_DELAY_MS),
		retryCondition: (error) => {
			return (
				getNetworkRequestIntent(error.config) === "background" &&
				axiosRetry.isNetworkError(error)
			)
		},
	})

	client.interceptors.response.use(
		(response) => response,
		(error: unknown) => {
			if (
				axios.isAxiosError(error) &&
				isNetworkConnectivityError(error) &&
				getNetworkRequestIntent(error.config) === "active" &&
				!error.config?.suppressOfflineToast
			) {
				showActiveNetworkErrorToast()
			}

			return Promise.reject(error)
		},
	)
}
